import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

interface Bank {
  id: number;
  bank_name: string;
  account_name: string;
  account_number: string;
  iban: string;
}
interface Pkg {
  id: number;
  name_ar: string;
  duration_days: number | null;
  price: number | string;
  display_style: string;
}

const formatPrice = (num: number) =>
  num.toLocaleString('ar-SA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export default function PinAdPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isAuthenticated, isLoading } = useAuth();

  const [packages, setPackages] = useState<Pkg[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const [packageId, setPackageId] = useState('');
  const [bankId, setBankId] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payDate, setPayDate] = useState('');
  const [reference, setReference] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // للبائعين فقط
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      toast.error('يجب تسجيل الدخول');
      navigate('/login');
    } else if (user && user.is_seller === false) {
      toast.error('خدمة التثبيت متاحة للبائعين فقط');
      navigate('/my/seller-profile');
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [pkgRes, bankRes] = await Promise.allSettled([
        api.get('/promotion-packages'),
        api.get('/bank-accounts'),
      ]);
      if (pkgRes.status === 'fulfilled') {
        const list: Pkg[] = pkgRes.value.data?.data || [];
        const pinned = list.filter((p) => p.display_style === 'pinned');
        setPackages(pinned.length ? pinned : list);
      } else {
        setDisabled(true);
      }
      if (bankRes.status === 'fulfilled') setBanks(bankRes.value.data?.data || []);
    } catch {
      setDisabled(true);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.is_seller === false) return;
    loadData();
  }, [isAuthenticated, user, loadData]);

  if (!isLoading && (!isAuthenticated || (user && user.is_seller === false))) return null;

  const onReceipt = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReceipt(e.target.files?.[0] || null);
  };

  const submit = async () => {
    if (!packageId) { toast.error('اختر مدة التثبيت'); return; }
    if (!bankId) { toast.error('اختر الحساب المُحوّل إليه'); return; }
    if (!payerName.trim()) { toast.error('أدخل اسم الدافع'); return; }
    if (!payDate) { toast.error('اختر تاريخ الدفع'); return; }
    if (!receipt) { toast.error('أرفق صورة الإيصال'); return; }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('package_id', packageId);
      fd.append('bank_account_id', bankId);
      fd.append('payer_name', payerName);
      fd.append('payment_date', payDate);
      if (reference.trim()) fd.append('transfer_reference', reference.trim());
      fd.append('receipt_image', receipt);
      await api.post('/ads/' + id + '/promote', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم إرسال طلب التثبيت مع إثبات الدفع، سيتم التفعيل بعد المراجعة', { duration: 6000 });
      navigate('/my/ads');
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        toast.error(Array.isArray(first) ? (first[0] as string) : String(first));
      } else {
        toast.error(err?.response?.data?.message || 'تعذّر إرسال الطلب');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selected = packages.find((p) => String(p.id) === packageId);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📌</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">تثبيت الإعلان</h1>
          <p className="text-gray-500 mt-2">ثبّت إعلانك أعلى نتائج القسم لمدة محددة مقابل رسوم</p>
        </div>

        {loadingData ? (
          <div className="text-center text-gray-400 py-10">جاري التحميل...</div>
        ) : disabled ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <span className="text-3xl">🚧</span>
            <p className="font-bold text-gray-800 mt-3">خدمة التثبيت غير متاحة حالياً</p>
            <p className="text-sm text-gray-500 mt-1">سيتم تفعيلها قريباً</p>
            <button onClick={() => navigate('/my/ads')} className="mt-5 text-primary-600 font-semibold text-sm hover:underline">← الرجوع لإعلاناتي</button>
          </div>
        ) : (
          <>
            {/* اختيار المدة */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 text-lg mb-4">اختر مدة التثبيت</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {packages.map((p) => {
                  const price = typeof p.price === 'string' ? parseFloat(p.price) : p.price;
                  const active = String(p.id) === packageId;
                  return (
                    <button key={p.id} onClick={() => setPackageId(String(p.id))}
                      className={`rounded-xl border-2 p-4 text-center transition ${active ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}>
                      <div className="text-2xl mb-1">📌</div>
                      <div className="font-bold text-gray-900 text-sm">{p.duration_days} يوم</div>
                      <div className="text-primary-700 font-bold mt-1">{formatPrice(price)} <span className="text-xs font-semibold">ر.س</span></div>
                    </button>
                  );
                })}
              </div>
              {packages.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-3">لا توجد باقات تثبيت متاحة حالياً</p>
              )}
            </div>

            {/* الحسابات البنكية */}
            {banks.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="font-bold text-gray-900 text-lg mb-4">الحسابات البنكية للتحويل</h2>
                <div className="space-y-3">
                  {banks.map((bank) => (
                    <div key={bank.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center"><span className="text-lg">🏦</span></div>
                        <div>
                          <p className="font-bold text-gray-900">{bank.bank_name}</p>
                          <p className="text-xs text-gray-500">{bank.account_name}</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                          <span className="text-gray-500">رقم الحساب</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900" dir="ltr">{bank.account_number}</span>
                            <button onClick={() => { navigator.clipboard?.writeText(bank.account_number); toast.success('نُسخ'); }} className="text-primary-600 text-xs" title="نسخ">📋</button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                          <span className="text-gray-500">IBAN</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900 text-xs" dir="ltr">{bank.iban}</span>
                            <button onClick={() => { navigator.clipboard?.writeText(bank.iban); toast.success('نُسخ'); }} className="text-primary-600 text-xs" title="نسخ">📋</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* نموذج الدفع */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 text-lg mb-1">بيانات الدفع (تحويل بنكي)</h2>
              <p className="text-xs text-gray-500 mb-5">بعد تحويل رسوم التثبيت لأحد الحسابات، سجّل بيانات الدفع وأرفق الإيصال ليتم التفعيل.</p>

              <div className="space-y-4">
                {selected && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex justify-between">
                    <span>{selected.duration_days} يوم تثبيت</span>
                    <span className="font-bold">{formatPrice(typeof selected.price === 'string' ? parseFloat(selected.price) : selected.price)} ر.س</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">الحساب المُحوّل إليه *</label>
                    <select value={bankId} onChange={(e) => setBankId(e.target.value)}
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm bg-white">
                      <option value="">اختر الحساب</option>
                      {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">اسم الدافع *</label>
                    <input type="text" value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="اسم صاحب الحوالة" maxLength={150}
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">تاريخ الدفع *</label>
                    <input type="date" value={payDate} max={today} onChange={(e) => setPayDate(e.target.value)}
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">رقم العملية / المرجع <span className="text-gray-400 text-xs font-normal">(اختياري)</span></label>
                    <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="رقم مرجع الحوالة إن وجد" maxLength={100} dir="ltr"
                      className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm text-left" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">إيصال العملية *</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-primary-300 transition cursor-pointer"
                    onClick={() => document.getElementById('pin-receipt-input')?.click()}>
                    {receipt ? (
                      <div>
                        <span className="text-2xl">🧾</span>
                        <p className="text-sm text-primary-600 font-semibold mt-1">{receipt.name}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">اضغط لتغيير الصورة</p>
                      </div>
                    ) : (
                      <div>
                        <span className="text-2xl">📸</span>
                        <p className="text-sm text-gray-500 mt-1">اضغط لرفع صورة الإيصال</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG — حد أقصى 3 ميقا</p>
                      </div>
                    )}
                  </div>
                  <input id="pin-receipt-input" type="file" accept="image/*" onChange={onReceipt} className="hidden" />
                </div>

                <button onClick={submit} disabled={submitting}
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-xl font-bold text-base transition">
                  {submitting ? 'جاري الإرسال...' : 'إرسال طلب التثبيت'}
                </button>
              </div>
            </div>
          </>
        )}

        <div className="text-center pb-4">
          <button onClick={() => navigate('/my/ads')} className="text-primary-600 hover:text-primary-700 font-semibold text-sm hover:underline">
            ← الرجوع لإعلاناتي
          </button>
        </div>
      </div>
    </div>
  );
}
